import React from 'react';
import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import Typography from '@material-ui/core/Typography';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import { Query } from 'react-apollo';
import Spin from 'components/common/Spin';
import { GET_SNAPSHOTS } from 'graphql/student/worksheets';
import Helmet from 'react-helmet';

const useStyles = makeStyles((theme) => ({
  root: {
    '& .MuiTableCell-root': {
      border: '1px solid #e0e0e0',
    },
  },
  table: {
    'display': 'flex',
    'flex': 1,
    flexWrap: 'nowrap',
    overflowX: 'scroll',
    '& > div:last-child': {
      borderRight: 'none',
    },
  },
  column: {
    flex: 1,
    borderRight: '1px solid #eee',
    padding: theme.spacing(2),
  },
  subheader: {
    fontWeight: 500,
  },
  subTable: {
    display: 'table',
    width: '100%',
    '& .row': {
      display: 'table-row',
      '&:nth-child(even)': {
        backgroundColor: '#F8F9FC'
      },
    },
    '& .th': {
      display: 'table-cell',
      padding: 6,
      fontWeight: 500,
    },
    '& .td': {
      display: 'table-cell',
      whiteSpace: 'nowrap',
      padding: 6,
    },
  },
}));

const Accordion = withStyles({
  root: {
    'marginBottom': 32,
    'borderTopLeftRadius': 16,
    'borderTopRightRadius': 16,
    'borderBottomLeftRadius': 16,
    'borderBottomRightRadius': 16,
    '&:before': {
      display: 'none',
    },
    '&$expanded': {
      marginBottom: 32,
    },
    '&:last-child': {
      borderBottomLeftRadius: 16,
      borderBottomRightRadius: 16,
    },
    '&:first-child': {
      borderTopLeftRadius: 16,
      borderTopRightRadius: 16,
    },
    'expanded': {
      margin: 'inherit',
    },
  },
})(ExpansionPanel);

const AccordionSummary = withStyles({
  root: {
    '&$expanded': {
      minHeight: 56,
      borderBottom: '1px solid #eee',
    },
    '& h6': {
      fontWeight: '600',
      fontSize: '1.2rem',
      marginLeft: 16,
    },
  },
  content: {
    alignItems: 'center',
  },
  expanded: {},
})(ExpansionPanelSummary);

const AccordionDetails = withStyles((theme) => ({
  root: {
    padding: 0,
  },
}))(ExpansionPanelDetails);

const SnapshotTable = ({ student }) => {
  const classes = useStyles();
  const [collpased, setCollapsed] = React.useState([]);

  const handleChange = (panel) => (event, isExpanded) => {
    if (isExpanded) {
      setCollapsed( collpased.filter(i => i !== panel));
    } else {
      setCollapsed([...collpased, panel]);
    }
  };

  return (
    <div className={classes.root}>
      <Helmet title="360 Snapshot" />
      <Query
        query={GET_SNAPSHOTS}
        variables={{ userId: student.id }}
        fetchPolicy={'cache-and-network'}
      >
        {({ data, loading, error }) => {
          if (loading) return <Spin spinning linear />;
          if (error) return 'Error';
          const { snapshots } = data;

          if (!snapshots.length) return 'No Snapshots found.';

          const parsedBocks = [];
          snapshots.forEach((s) => {
            const parsed = [];
            s.response.forEach((block) => {
              const row = {
                id: block.id,
              };
              switch (block.type) {
                case 'QUESTIONS_TABLE': {
                  const { layout, columns, rows, values } = block;
                  if (layout === 'row') {
                    row.header = columns[0].header;
                    row.values = rows.map((r, idx) => ({
                      ...r,
                      value: values?.[idx],
                    }));
                  } else {
                    row.header = block.description;
                    row.content = (
                      <div className={classes.subTable}>
                        <div className="row">
                          {columns?.map((c, idx) => (
                            <Typography
                              color="textSecondary"
                              variant="caption"
                              className="th"
                              key={idx}
                            >
                              {c.header}
                            </Typography>
                          ))}
                        </div>
                        {values?.map(r => (
                          <div className="row">
                            {r.map((i) => (
                              <Typography variant="body2" className="td" color="textPrimary">
                                {i}
                              </Typography>
                            ))}
                          </div>
                        ))}
                      </div>
                    );
                  }
                  break;
                }
                default: {
                  console.log('not handled', block);
                }
              }
              if (row.header) {
                parsed.push(row);
              }
            });
            parsedBocks.push(parsed);
          });
          const firstSnapshot = parsedBocks[0];

          return (
            <div>
              {firstSnapshot.map((e, idx) => {
                const isExpanded = !collpased.includes(idx);
                return (
                  <Accordion
                    expanded={isExpanded}
                    onChange={handleChange(idx)}
                    key={idx}
                    component={Card}
                  >
                    <AccordionSummary id={e.id}>
                      {isExpanded ? (
                        <RemoveCircleOutlineIcon color="disabled" />
                      ) : (
                        <AddCircleOutlineIcon color="disabled" />
                      )}
                      <Typography variant="h6">{e.header}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <div className={classes.table}>
                        {parsedBocks.map((blocks, i) => (
                          <div key={i} className={classes.column}>
                            <Box marginBottom={3}>
                              <Typography variant="subtitle2">
                                Snapshot #{i + 1}
                              </Typography>
                            </Box>
                            {blocks[idx]?.content || (
                              <div>
                                {blocks[idx]?.values?.map((block) => (
                                  <Box marginBottom={2} key={block.id}>
                                    <Typography
                                      color="textSecondary"
                                      variant="body2"
                                      className={classes.subheader}
                                    >
                                      {block.header}
                                    </Typography>
                                    <Typography
                                      color="textPrimary"
                                      variant="body2"
                                    >
                                      {block.value?.[0]}
                                    </Typography>
                                  </Box>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </div>
          );
        }}
      </Query>
    </div>
  );
};

export default SnapshotTable;
